import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdfOutlined';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Popover from '@mui/material/Popover';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React, { type FC, useEffect, useState } from 'react';
import {
  useGetQuickRepliesQuery,
  useUpdateCurrentUserMutation,
  useUploadQuickReplyFileMutation,
} from 'services/api/user.api';
import type { QuickReply, QuickReplyFile, QuickReplyText } from 'services/types/quickReply';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  SHADOW_CARD,
  SURFACE_CONTAINER_LOW,
  YELLOW,
} from 'styles/tokens';

interface Props {
  message: string;
  onMessageChange: (next: string) => void;
  inputRef: React.RefObject<HTMLTextAreaElement | null>;
  onSendFile: (qr: QuickReplyFile) => void;
  onAttachClick: () => void;
  disabled?: boolean;
}

const QuickReplies: FC<Props> = ({ message, onMessageChange, inputRef, onSendFile, onAttachClick, disabled }) => {
  const { data: serverItems } = useGetQuickRepliesQuery();
  const [updateCurrentUser] = useUpdateCurrentUserMutation();
  const [uploadFile, { isLoading: isUploading }] = useUploadQuickReplyFileMutation();

  const [items, setItems] = useState<QuickReply[]>([]);
  useEffect(() => {
    if (serverItems) setItems(serverItems);
  }, [serverItems]);
  const save = (next: QuickReply[]) => {
    setItems(next);
    updateCurrentUser({ quickReplies: next });
  };

  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newBody, setNewBody] = useState('');
  const newFileRef = React.useRef<HTMLInputElement>(null);

  const texts = items.filter((i): i is QuickReplyText => i.type === 'text');
  const files = items.filter((i): i is QuickReplyFile => i.type === 'file');

  const focus = () => inputRef.current?.focus();
  const closePanel = () => {
    setPanelOpen(false);
    setAdding(false);
    setNewTitle('');
    setNewBody('');
  };

  const insertText = (text: string) => {
    onMessageChange(message ? `${message}\n${text}` : text);
    focus();
  };
  const pickFile = (qr: QuickReplyFile) => {
    onSendFile(qr);
    closePanel();
  };

  const addText = () => {
    if (!newTitle.trim() || !newBody.trim()) return;
    save([{ title: newTitle.trim(), type: 'text', body: newBody.trim() }, ...items]);
    setAdding(false);
    setNewTitle('');
    setNewBody('');
  };
  const addFile = async (file: File) => {
    try {
      const { path, fileName, url } = await uploadFile(file).unwrap();
      save([{ title: fileName.replace(/\.[^.]+$/, ''), type: 'file', fileName, path, url }, ...items]);
    } catch (err) {
      console.error('Error uploading quick reply file', err);
    }
  };

  // "/" autocomplete: a slash-token at the start or after a space, filtered by title.
  const slashMatch = /(?:^|\s)\/([\w-]*)$/.exec(message);
  const slashItems = slashMatch ? items.filter((i) => i.title.toLowerCase().includes(slashMatch[1].toLowerCase())) : [];
  const applySlash = (qr: QuickReply) => {
    if (!slashMatch) return;
    const start = slashMatch.index + slashMatch[0].indexOf('/');
    onMessageChange(message.slice(0, start) + (qr.type === 'text' ? qr.body : ''));
    if (qr.type === 'file') onSendFile(qr);
    focus();
  };

  const rowSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    px: 1.25,
    py: 1,
    borderRadius: RADIUS_CARD,
    cursor: 'pointer',
    '&:hover': { backgroundColor: SURFACE_CONTAINER_LOW },
  } as const;

  return (
    <>
      {slashItems.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            right: 0,
            mb: 1,
            maxHeight: 260,
            overflowY: 'auto',
            backgroundColor: GLASS_BACKGROUND,
            backdropFilter: GLASS_BLUR,
            borderRadius: RADIUS_CARD,
            boxShadow: SHADOW_CARD,
            zIndex: 3,
            p: 0.5,
          }}
        >
          <Typography
            sx={{ px: 1.25, py: 0.5, fontSize: 11, fontWeight: 700, color: alpha(NAVY, 0.45), letterSpacing: 0.5 }}
          >
            RESPUESTAS RÁPIDAS
          </Typography>
          {slashItems.map((qr, i) => (
            <Box
              key={`${qr.title}-${i}`}
              onMouseDown={(e) => {
                e.preventDefault(); // keep focus in the textarea
                applySlash(qr);
              }}
              sx={{ ...rowSx, py: 0.75 }}
            >
              <Typography sx={{ fontSize: 13, color: NAVY, fontWeight: 700, minWidth: 110, flexShrink: 0 }} noWrap>
                {qr.title}
              </Typography>
              <Typography noWrap sx={{ fontSize: 13, color: alpha(NAVY, 0.6), flex: 1 }}>
                {qr.type === 'text' ? qr.body : `📎 ${qr.fileName}`}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      <IconButton
        size="small"
        tabIndex={-1}
        disabled={disabled}
        onClick={(e) => {
          setAnchor(e.currentTarget);
          setPanelOpen(true);
        }}
        sx={{ color: alpha(NAVY, 0.55) }}
      >
        <AddIcon />
      </IconButton>

      <Popover
        anchorEl={anchor}
        open={panelOpen}
        onClose={closePanel}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{ paper: { sx: { mt: -1, borderRadius: RADIUS_CARD, boxShadow: SHADOW_CARD, width: 340 } } }}
      >
        <Box sx={{ p: 1.5 }}>
          <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
            {texts.map((qr, i) => (
              <Box key={`${qr.title}-${i}`} sx={rowSx} onClick={() => insertText(qr.body)}>
                <ChatBubbleOutlineIcon fontSize="small" sx={{ color: alpha(NAVY, 0.45) }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ color: NAVY, fontWeight: 600, fontSize: 14 }}>{qr.title}</Typography>
                  <Typography noWrap sx={{ color: alpha(NAVY, 0.55), fontSize: 12 }}>
                    {qr.body}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    save(items.filter((i2) => i2 !== qr));
                  }}
                  sx={{ color: alpha(NAVY, 0.35) }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}

            {files.length > 0 && texts.length > 0 && <Divider sx={{ my: 0.5, borderColor: SURFACE_CONTAINER_LOW }} />}

            {files.map((qr, i) => (
              <Box key={`${qr.path}-${i}`} sx={rowSx} onClick={() => pickFile(qr)}>
                {qr.fileName.endsWith('.pdf') ? (
                  <Box
                    sx={{
                      width: 40,
                      height: 28,
                      borderRadius: 1,
                      backgroundColor: SURFACE_CONTAINER_LOW,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <PictureAsPdfIcon fontSize="small" sx={{ color: alpha(NAVY, 0.5) }} />
                  </Box>
                ) : (
                  <Box
                    component="img"
                    src={qr.url}
                    alt={qr.title}
                    sx={{
                      width: 40,
                      height: 28,
                      objectFit: 'cover',
                      borderRadius: 1,
                      backgroundColor: SURFACE_CONTAINER_LOW,
                    }}
                  />
                )}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ color: NAVY, fontWeight: 600, fontSize: 14 }}>{qr.title}</Typography>
                  <Typography noWrap sx={{ color: alpha(NAVY, 0.55), fontSize: 12 }}>
                    {qr.fileName}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    save(items.filter((i2) => i2 !== qr));
                  }}
                  sx={{ color: alpha(NAVY, 0.35) }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>

          {items.length > 0 && <Divider sx={{ my: 1, borderColor: SURFACE_CONTAINER_LOW }} />}

          {adding ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <InputBase
                autoFocus
                placeholder="Título"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderRadius: RADIUS_COIN,
                  backgroundColor: SURFACE_CONTAINER_LOW,
                  fontSize: 14,
                  color: NAVY,
                }}
              />
              <InputBase
                multiline
                minRows={2}
                placeholder="Mensaje"
                value={newBody}
                onChange={(e) => setNewBody(e.target.value)}
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderRadius: RADIUS_CARD,
                  backgroundColor: SURFACE_CONTAINER_LOW,
                  fontSize: 14,
                  color: NAVY,
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Typography
                  onClick={() => setAdding(false)}
                  sx={{ cursor: 'pointer', color: alpha(NAVY, 0.55), fontSize: 13, py: 0.5 }}
                >
                  Cancelar
                </Typography>
                <Box
                  onClick={addText}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: YELLOW,
                    color: NAVY,
                    fontWeight: 600,
                    fontSize: 13,
                    px: 2,
                    py: 0.5,
                    borderRadius: RADIUS_COIN,
                  }}
                >
                  Guardar
                </Box>
              </Box>
            </Box>
          ) : (
            <>
              <Box sx={{ ...rowSx, color: NAVY }} onClick={() => setAdding(true)}>
                <AddIcon fontSize="small" sx={{ color: alpha(NAVY, 0.6) }} />
                <Typography sx={{ color: NAVY, fontWeight: 600, fontSize: 14 }}>Nueva respuesta rápida</Typography>
              </Box>
              <Box
                sx={{
                  ...rowSx,
                  color: NAVY,
                  pointerEvents: isUploading ? 'none' : 'auto',
                  opacity: isUploading ? 0.6 : 1,
                }}
                onClick={() => newFileRef.current?.click()}
              >
                {isUploading ? (
                  <CircularProgress size={18} sx={{ color: alpha(NAVY, 0.6) }} />
                ) : (
                  <ImageOutlinedIcon fontSize="small" sx={{ color: alpha(NAVY, 0.6) }} />
                )}
                <Typography sx={{ color: NAVY, fontWeight: 600, fontSize: 14 }}>
                  {isUploading ? 'Subiendo…' : 'Nuevo archivo rápido'}
                </Typography>
              </Box>
              <input
                ref={newFileRef}
                type="file"
                accept=".jpg,.jpeg,.png,.mp4,.pdf"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) addFile(file);
                  e.target.value = '';
                }}
              />
              <Box
                sx={{ ...rowSx, color: NAVY }}
                onClick={() => {
                  closePanel();
                  onAttachClick();
                }}
              >
                <AttachFileIcon fontSize="small" sx={{ color: alpha(NAVY, 0.6) }} />
                <Typography sx={{ color: NAVY, fontWeight: 600, fontSize: 14 }}>
                  Adjuntar archivo desde tu equipo
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default QuickReplies;
